import { makeAutoObservable } from 'mobx';
import {
  Label,
  Milestone,
  RawProject,
  RawMilestone,
  RawTaskGroup,
  ProjectProgress,
  RawProjectFile
} from '../interfaces/project';
import { rootStore } from '../rootStore';
import { ProjectResourceDetailInterface, StatusListInterface } from '../../interfaces/ProjectInterface';
import { CustomerDetailsInterface } from '../../interfaces/CustomerInterface';
import { determineProjectStatus, getDefaultProgress } from '../../services/projectServices';
import { NewTaskFilterInterface } from '../../interfaces/TaskInterface';
import { applyTaskFilter, filterCompletedTasksByDate, getWeekTasks } from '../../services/mobxTaskFilterService';
import { mathRound } from '../../helpers/common';
import {
  PROJECT_STATUS_TEXT,
  SHOW_COMPLETED_TASK_BY_DATE,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_TYPE,
  TASK_LIST_TYPE
} from '../../global/constants';
import moment from 'moment';

export class ProjectModel {
  // Common
  id: string;
  name: string;
  description: string | null;
  taskFilterType: NewTaskFilterInterface;

  // Dates
  updatedOn: Date;
  createdOn: Date;
  dueDate: Date | null;

  // Relational
  createdById: string;
  customerId: string;
  companyId: string;
  usersIds: string[];

  // Others
  index: number;
  isArchived: boolean;
  isEstimate: boolean;
  isFavorite: boolean;
  isGroupEnabled: boolean;
  // isPriority: boolean;
  isMilestone: boolean;
  labelsEnabled: boolean;
  labelsList: Label[];
  multiAssignee: boolean;
  priority: {
    enabled: boolean;
    default: number;
  };
  statusData: StatusListInterface[];
  statusEnable: boolean;
  customer: CustomerDetailsInterface | null;
  githubRepository: number | null;
  resources: ProjectResourceDetailInterface[];
  milestones: Milestone[] = [];
  taskGroups: RawTaskGroup[] = [];
  startOfTheWeek: Date = new Date();
  files: RawProjectFile[] = [];
  currentFolderId: string | null = null;
  archiveMilestoneList: Milestone[] = [];

  constructor(rawProject: RawProject) {
    makeAutoObservable(this, { filteredProjectTasks: false });

    // Common
    this.id = rawProject.id;
    this.name = rawProject.name;
    this.description = rawProject.description;
    this.createdOn = new Date(rawProject.createdOn);
    this.updatedOn = new Date(rawProject.updatedOn);
    this.dueDate = rawProject.dueDate ? new Date(rawProject.dueDate) : null;
    this.taskFilterType = {
      showSubTask: false,
      showCompletedTaskByFilter: SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH,
      showRecentlyCompletedTask: true,
      showEmptyGroup: true,
      taskViewType: TASK_LIST_TYPE.TASKS,
      groupingStatus: SHOW_TASK_GROUPING_TYPE.DEFAULT,
      orderByStatus: SHOW_TASK_BY_ORDER.CUSTOM
    };

    // Relational
    this.createdById = rawProject.createdBy;
    this.customerId = rawProject.customerId;
    this.companyId = rawProject.companyId;
    this.usersIds = rawProject.users;

    // Others
    this.index = this.createdOn.getTime();
    this.isArchived = rawProject.isArchived ?? false;
    this.isEstimate = rawProject.isEstimate ?? false;
    this.isFavorite = rawProject.isFavorite ?? false;
    this.isGroupEnabled = rawProject.isGroupEnabled ?? false;
    // this.isPriority = rawProject.isPriority ?? false;
    this.isMilestone = rawProject.isMilestone ?? false;
    this.labelsEnabled = rawProject.labelsEnabled || false;
    this.labelsList = rawProject.labelsList || [];
    this.multiAssignee = rawProject.multiAsignee || false;
    this.priority = rawProject.priority ?? { enabled: false, default: 0 };
    this.statusData = rawProject.status ?? [];
    this.statusEnable = rawProject.statusEnable ?? false;
    // this.taskGroups = rawProject.taskGroups ?? [];
    // this.milestones = rawProject.milestones ?? [];
    this.customer = rawProject.customer ?? null;
    this.githubRepository = rawProject.githubRepository ?? null;
    this.resources = rawProject.resources ?? [];
    this.currentFolderId = null;
  }

  get users() {
    return rootStore.workspaceStore.currentWorkspace?.users.filter((user) => this.usersIds.includes(user.id)) || [];
  }

  get defaultStatus() {
    return this.statusData.find((status) => status.Default) || this.statusData[0] || {};
  }

  get tasks() {
    return rootStore.taskStore.tasks.filter((task) => task.projectId === this.id) ?? [];
  }

  get filteredTask() {
    return this.tasks.filter((task) => !task.parentTaskId);
  }

  get progressData(): ProjectProgress {
    if (this.tasks.length === 0) {
      return getDefaultProgress();
    }

    const completedTasks = this.tasks.filter((x: { status: boolean }) => x?.status)?.length || 0;
    const progress = this.tasks.length === 0 ? 0 : Number(mathRound((completedTasks / this.tasks.length) * 100, 0));
    const status = determineProjectStatus(this.tasks);

    return {
      progress,
      completedTasks,
      totalTasks: this.tasks.length,
      status,
      statusText: PROJECT_STATUS_TEXT[status]
    };
  }

  get filteredProjectTasks() {
    const parentTasks = filterCompletedTasksByDate(this);
    return applyTaskFilter(parentTasks, this);
  }

  get weekWiseTasks() {
    const startOfWeek = moment(this.startOfTheWeek).startOf('week').format('YYYY-MM-DD');
    const endOfWeek = moment(this.startOfTheWeek).endOf('week').format('YYYY-MM-DD');
    const weekTasks = this.tasks.filter((task) => {
      const taskDate = moment(task.dueDate).format('YYYY-MM-DD');
      return taskDate >= startOfWeek && taskDate <= endOfWeek;
    });
    const weekWiseTasks = getWeekTasks(this.startOfTheWeek, weekTasks, this.taskFilterType) || [];
    return weekWiseTasks;
  }

  get folderWiseFiles() {
    if (this.currentFolderId) {
      return this.files
        .filter((file) => file.parentId === this.currentFolderId)
        .toSorted((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
    }

    return this.files
      .filter((file) => !file.parentId)
      .toSorted((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
  }

  setStartOfTheWeek(startOfTheWeek: Date) {
    this.startOfTheWeek = startOfTheWeek;
  }

  setFavorite(isFavorite: boolean) {
    this.isFavorite = isFavorite;
  }

  setIndex(index: number) {
    this.index = index;
  }

  addUser(userId: string) {
    this.usersIds.push(userId);
  }

  removeUser(userId: string) {
    this.usersIds = this.usersIds.filter((id) => id !== userId);
  }

  addResource(resource: ProjectResourceDetailInterface) {
    this.resources.push(resource);
  }

  removeResource(resourceId: string) {
    this.resources = this.resources.filter((r) => r['_id'] !== resourceId);
  }

  updateResource(resourceId: string, resourceUpdate: { reference?: string; label?: string }) {
    this.resources = this.resources.map((r) => {
      if (r['_id'] === resourceId) {
        const Label = resourceUpdate.label ?? r.Label;
        const Reference = resourceUpdate.reference ?? r.Reference;
        return { ...r, Label, Reference };
      }
      return r;
    });
  }

  setGroupEnabled(isGroupEnabled: boolean) {
    this.isGroupEnabled = isGroupEnabled;
  }

  setPriorityEnabled(priorityEnabled: boolean) {
    this.priority.enabled = priorityEnabled;
  }

  setDefaultPriority(defaultPriority: number) {
    this.priority.default = defaultPriority;
  }

  setStatusEnabled(statusEnabled: boolean) {
    this.statusEnable = statusEnabled;
  }

  setLabelsEnabled(labelsEnabled: boolean) {
    this.labelsEnabled = labelsEnabled;
  }

  setMultiAssignee(multiAssignee: boolean) {
    this.multiAssignee = multiAssignee;
  }

  setEstimate(isEstimate: boolean) {
    this.isEstimate = isEstimate;
  }

  setMilestone(isMilestone: boolean) {
    this.isMilestone = isMilestone;
  }

  updateTaskFilterType<K extends keyof NewTaskFilterInterface>(propsName: K, value: NewTaskFilterInterface[K]) {
    this.taskFilterType[propsName] = value;
  }

  setMilestonesList(milestones: RawMilestone[]) {
    this.milestones = milestones
      .map((milestone) => {
        const tasks = this.tasks.filter((task) => milestone.taskIds.includes(task.id));
        let progressData = getDefaultProgress();
        if (tasks.length > 0) {
          const completedTasks = tasks.filter((x: { status: boolean }) => x?.status)?.length || 0;
          const progress = tasks.length === 0 ? 0 : Number(mathRound((completedTasks / tasks.length) * 100, 0));
          const status = determineProjectStatus(tasks);
          progressData = {
            progress,
            completedTasks,
            totalTasks: tasks.length,
            status,
            statusText: PROJECT_STATUS_TEXT[status]
          };
        }
        return {
          ...milestone,
          tasks,
          users: this.users.filter((user) => milestone.userIds.includes(user.id)),
          progressData
        };
      })
      .toSorted((a, b) => {
        return a.index - b.index;
      });
  }

  setArchiveMilestoneList(archiveMilestoneList: RawMilestone[]) {
    this.archiveMilestoneList = archiveMilestoneList
      .map((milestone) => {
        return {
          ...milestone,
          tasks: this.tasks.filter((task) => milestone.taskIds.includes(task.id)),
          users: this.users.filter((user) => milestone.userIds.includes(user.id)),
          progressData: getDefaultProgress()
        };
      })
      .toSorted((a, b) => {
        return a.index - b.index;
      });
  }

  updateMilestone(milestoneId: string, updatedMilestone: Milestone) {
    this.milestones = this.milestones
      .map((milestone) => {
        if (milestone.id === milestoneId) {
          milestone = updatedMilestone;
        }
        return milestone;
      })
      .toSorted((a, b) => {
        return a.index - b.index;
      });
  }

  archiveMilestone(milestoneId: string) {
    const milestone = this.milestones.find((milestone) => milestone.id === milestoneId);
    if (milestone) {
      this.milestones = this.milestones.filter((milestone) => milestone.id !== milestoneId);
      this.archiveMilestoneList.push(milestone);
    }
  }

  setTaskGroups(taskGroups: RawTaskGroup[]) {
    this.taskGroups = taskGroups;
  }

  setTaskFilterType(taskFilterType: NewTaskFilterInterface) {
    this.taskFilterType = taskFilterType;
  }

  updateProjectFilter() {
    const filter = rootStore.projectStore.getProjectFilter(this.id);
    if (filter) {
      this.taskFilterType = filter;
    }
  }

  setCurrentFolderId(currentFolderId: string | null) {
    this.currentFolderId = currentFolderId;
  }

  setProjectFiles(files: RawProjectFile[]) {
    this.files = files;
  }
}

export const createObservableProject = (rawProject: RawProject) => {
  return new ProjectModel(rawProject);
};
